import React, { useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { TypeEnum } from "@/src/api/ProjectAPI.types";

import { apiToOfferForm, offerFormToApi } from "./offerAdapter";
import OfferPlans from "./OfferPlans";
import OfferSelectBox from "./OfferSelectBox";
import OfferTranslations from "./OfferTranslations";
import { CONTENT_MAX_WIDTH_STYLE } from "../../constants/size";
import "../../css/admin/utils/item-status.scss";
import { isGeoblockActive } from "../../utils/geoblock";
import Block from "../block/Block";
import TrailerSelect from "../contentTabs/metadata/TrailerSelect";
import Breadcrumbs from "../utils/Breadcrumbs";
import ContainerHeader from "../utils/ContainerHeader";
import InfoTooltip from "../utils/InfoTooltip";

import type { ChangeEvent, JSX } from "react";

import type { AppState } from "@/src/reducers";

import type { OfferPageAction } from "./OffersEdit";
import type { OfferTranslationsHandle } from "./OfferTranslations";
import type { GetOfferResponse, UpdatedOffer } from "../../api/OfferAPI.types";
import type { OfferPlanType } from "../../types";
import type { TrailerOption } from "../contentTabs/metadata/TrailerSelect";
import type { SelectBoxDisplayItem } from "../selectBox/selectBox.types";

const containerHeaderLinks = {
  en: "https://support.okast.tv/en/support/search/solutions?term=Manage+offers+%26+monetization",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000097708-g%C3%A9rer-ses-offres-et-sa-mon%C3%A9tisation",
};

interface OffersFormProps {
  action: OfferPageAction;
  item: GetOfferResponse;
  onSubmit: (offer: UpdatedOffer, disableValidation: boolean) => Promise<void>;
}

function OffersForm({ action, item, onSubmit }: OffersFormProps): JSX.Element {
  const { t } = useTranslation(["editor-offers"]);
  const [offer, setOffer] = useState(() => apiToOfferForm(item));
  const translationsRef = useRef<OfferTranslationsHandle>(null);
  const isEduProject = useSelector(
    (state: AppState) =>
      state.architectureReducer.project?.type === TypeEnum.Edu,
  );

  const maxOfferRank = item?.total_offers_count;

  const breadcrumbs = [
    {
      name: t("editor-menu:offerManagement"),
      url: "/offers",
    },
    {
      name: t(action),
    },
  ];

  useEffect(() => {
    if (item) {
      setOffer(apiToOfferForm(item));
    }
  }, [item]);

  const handleChangeLabel = (value: string): void => {
    setOffer((prevOffer) => ({ ...prevOffer, label: value }));
  };

  const handleChangeEnabled = (value: boolean): void => {
    setOffer((prevOffer) => ({ ...prevOffer, enabled: value, visible: value }));
  };

  const handleSave = async (): Promise<void> => {
    const isValid = (await translationsRef.current?.isValid()) ?? true;
    if (!isValid) return;

    const { translations, translations_indexes: translationsIndexes } =
      translationsRef.current?.getApiTranslations?.() ?? {
        translations: item.translations,
        translations_indexes: item.translations_indexes,
      };

    const updatedOfferPlanList = offer.offer_plans.map(
      (plan): OfferPlanType => {
        if (
          isGeoblockActive(plan.geoblock_mode ?? "disabled") &&
          Array.isArray(plan.countries) &&
          plan.countries.length === 0
        ) {
          return { ...plan, geoblock_mode: "disabled" };
        }
        return plan;
      },
    );

    await onSubmit(
      {
        ...item,
        ...offerFormToApi({ ...offer, offer_plans: updatedOfferPlanList }),
        translations,
        translations_indexes: translationsIndexes,
      },
      false,
    );

    translationsRef.current?.onSaveSuccess();
  };

  const onAddMedias = useCallback((medias: SelectBoxDisplayItem[]): void => {
    setOffer((prevOffer) => ({
      ...prevOffer,
      medias: medias.map((media) => ({ uuid: media.uuid })),
    }));
  }, []);

  const handleRankChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;

    const intValue = value ? parseInt(value, 10) : 0;
    const rank = Math.min(Math.max(intValue, 0), maxOfferRank);

    setOffer((prevOffer) => ({ ...prevOffer, rank }));
  };

  const handleChangeTrailer = (option: TrailerOption | null): void => {
    setOffer((prevOffer) => ({
      ...prevOffer,
      media_preview_uuid: option?.value ?? null,
    }));
  };

  return (
    <div
      className="editor-offer-form-container editor-has-breadcrumbs mx-auto flex max-w-(--maxWidth) flex-col gap-3.75"
      style={CONTENT_MAX_WIDTH_STYLE}
    >
      <Breadcrumbs items={breadcrumbs} />
      <ContainerHeader
        withBackBtn
        infoLinks={containerHeaderLinks}
        item={offer}
        itemStatus={offer.enabled ? "active" : "stop"}
        onChangeState={handleChangeEnabled}
        onSave={handleSave}
        onChangeLabel={(e: ChangeEvent<HTMLInputElement>["target"]): void => {
          handleChangeLabel(e.value);
        }}
      />
      <OfferTranslations ref={translationsRef} offer={item} />
      <Block title={t("options")}>
        <div className="editor-form-section">
          <div className="editor-rank-module">
            <div className="mb-2 flex items-center">
              <div className="text-sm">{t("rank")}</div>
              <InfoTooltip>{t("rankTooltip")}</InfoTooltip>
            </div>
            <input
              max={maxOfferRank}
              min={0}
              onChange={handleRankChange}
              type="number"
              value={offer.rank}
            />
          </div>
        </div>
        <div className="mt-9 flex flex-col gap-2.75">
          <div className="text-sm">{t("addMediaPreviewToOffer")}</div>
          <TrailerSelect
            onChange={handleChangeTrailer}
            trailerId={offer.media_preview_uuid}
          />
        </div>
      </Block>
      <Block title={t("elements")}>
        <OfferSelectBox item={offer} onChangeItems={onAddMedias} />
      </Block>
      <Block title={t(isEduProject ? "configurationTitle" : "pricingTitle")}>
        <OfferPlans offer={offer} setOffer={setOffer} />
      </Block>
    </div>
  );
}

export default OffersForm;
